<script setup lang="ts">
/**
 * LiveAnnouncer — região aria-live para anunciar mudanças de estado
 * (transições entre Onboarding/Home/Conversation/etc) a leitores de tela.
 *
 * Constitucional:
 * - F2 (Fase 0 Lume): leitor de tela recebe feedback explícito de mudanças.
 * - aria-live="polite" para não interromper; "assertive" reservado para erros.
 * - Princípio X: dignidade — usuário sabe sempre onde está.
 */
import { ref, watch } from "vue";

interface Props {
  /** Mensagem a anunciar (string vazia = sem anúncio). */
  message: string;
  /** Polite (default) ou assertive para erros. */
  urgency?: "polite" | "assertive";
}

const props = withDefaults(defineProps<Props>(), { urgency: "polite" });
const liveMessage = ref("");

watch(
  () => props.message,
  (msg) => {
    if (!msg) return;
    // Reset para forçar novo anúncio (SR re-anuncia se texto mudar)
    liveMessage.value = "";
    requestAnimationFrame(() => {
      liveMessage.value = msg;
    });
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="hlos-sr-only"
    role="status"
    :aria-live="urgency"
    aria-atomic="true"
    data-testid="live-announcer"
  >
    {{ liveMessage }}
  </div>
</template>

<style scoped>
.hlos-sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
